import { getUserList, putBlockUser, putDeleteUser, putUnblockUser, putUpdateWallet } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";

const UserManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [editingKlav, setEditingKlav] = useState<string>("");
    const itemsPerPage = 10;

    const { data, isLoading, isError } = useQuery(
        ["userList", currentPage, itemsPerPage],
        () => getUserList(currentPage, itemsPerPage),
        { keepPreviousData: true }
    );

    if (isLoading) return <div className="p-8"><div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" /></div>;
    if (isError) return <div className="p-8 text-red-500">오류가 발생했습니다.</div>;

    const userList = data?.data.data.items || [];
    const totalItems = data?.data.data.total || 0;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);
    const toggleRow = (id: string) => {
        setExpandedRow(expandedRow === id ? null : id);
        setEditingKlav("");
    };

    const handleAction = async (action: () => Promise<any>, msg: string) => {
        if (!(await showConfirm(`${msg} 하시겠습니까?`, { title: msg, confirmText: '확인', variant: 'warning' }))) return;
        const response = await action();
        if (response.data.isSuccess === 'T') {
            showAlert("처리 완료되었습니다.", { variant: 'success' });
            queryClient.invalidateQueries(["userList"]);
        }
    };

    const handleUpdateWallet = async (userId: string, currentKlav: number) => {
        if (!editingKlav) { showAlert("차감할 클랩을 입력해주세요.", { variant: 'warning' }); return; }
        const klav = Number(editingKlav);
        if (isNaN(klav)) { showAlert("숫자만 입력 가능합니다.", { variant: 'warning' }); return; }
        if (klav > currentKlav) { showAlert("보유 클랩보다 많은 양을 차감할 수 없습니다.", { variant: 'error' }); return; }
        const response = await putUpdateWallet(userId, klav);
        if (response.data.isSuccess === 'T') {
            showAlert("차감 완료되었습니다.", { variant: 'success' });
            queryClient.invalidateQueries(["userList"]);
            setEditingKlav("");
        }
    };

    const StatusBadge = ({ active, trueText, falseText }: { active: boolean, trueText: string, falseText: string }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
            ${active ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {active ? trueText : falseText}
        </span>
    );

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">유저 관리</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}명의 유저</p>
            </div>

            <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-achromatic-100 bg-achromatic-50">
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">#</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">이름</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">이메일</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">이용 상태</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">계정 상태</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">권한</th>
                        </tr>
                    </thead>
                    <tbody>
                        {userList.map((item: any, index: number) => (
                            <>
                                <tr
                                    key={item.user.id}
                                    className={`border-b border-achromatic-50 cursor-pointer transition-colors hover:bg-primary-50/30
                                        ${expandedRow === item.user.id ? 'bg-primary-50/20' : ''}`}
                                    onClick={() => toggleRow(item.user.id)}
                                >
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                    <td className="py-3 px-4 text-[13px] font-medium text-achromatic-800">{item.user.name}</td>
                                    <td className="py-3 px-4 text-[13px] text-achromatic-500">{item.user.email}</td>
                                    <td className="py-3 px-4 text-center">
                                        <StatusBadge active={item.user.isBlocked} trueText="정지" falseText="정상" />
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <StatusBadge active={item.user.isDeleted} trueText="탈퇴" falseText="활동" />
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                                            ${item.user.role === 'creator' ? 'bg-purple-50 text-purple-600' : 'bg-achromatic-100 text-achromatic-500'}`}>
                                            {item.user.role === 'creator' ? '크리에이터' : '유저'}
                                        </span>
                                    </td>
                                </tr>

                                {expandedRow === item.user.id && (
                                    <tr key={`expanded-${item.user.id}`} className="bg-achromatic-50/50">
                                        <td colSpan={6} className="p-6">
                                            <div className="grid grid-cols-2 gap-6">
                                                {/* User Info */}
                                                <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                    <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">고객 정보</h3>
                                                    <div className="flex flex-col gap-1.5 text-[13px]">
                                                        <div className="flex"><span className="text-achromatic-400 w-20">닉네임</span><span className="font-medium text-achromatic-700">{item.user.name}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">실명</span><span className="font-bold text-achromatic-900">{item.user.realName || '-'}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">주민번호</span><span className="font-mono font-medium text-achromatic-900">{item.user.residentNumber || '-'}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">환급계좌</span><span className="font-medium text-achromatic-700">{item.user.bankName ? `${item.user.bankName} ${item.user.accountNumber} (${item.user.accountHolder || ''})` : '-'}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">이메일</span><span className="font-medium text-achromatic-700">{item.user.email}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">생년월일</span><span className="font-medium text-achromatic-700">{item.user.birth || '-'}</span></div>
                                                        <div className="flex"><span className="text-achromatic-400 w-20">전화번호</span><span className="font-medium text-achromatic-700">{item.user.phone || '-'}</span></div>
                                                    </div>
                                                </div>

                                                {/* Wallet Info */}
                                                <div className="bg-white rounded-xl border border-achromatic-100 p-4">
                                                    <h3 className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">지갑 정보</h3>
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <span className="text-[13px] text-achromatic-400">보유 잔액</span>
                                                        <span className="text-lg font-extrabold text-achromatic-900">{item.wallet?.klav ?? 0}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            className="flex-1 px-3 py-2 border border-achromatic-200 rounded-lg text-[13px] focus:outline-none focus:border-primary-500"
                                                            placeholder="차감할 금액"
                                                            value={editingKlav}
                                                            onChange={(e) => setEditingKlav(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <button
                                                            className="px-4 py-2 bg-red-500 text-white text-[12px] font-bold rounded-lg hover:bg-red-600 transition-colors"
                                                            onClick={(e) => { e.stopPropagation(); handleUpdateWallet(item.user.id, item.wallet?.klav ?? 0); }}
                                                        >
                                                            차감
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex justify-end gap-2 mt-4">
                                                {item.user.isBlocked ? (
                                                    <button className="px-4 py-2 bg-emerald-500 text-white text-[12px] font-bold rounded-lg hover:bg-emerald-600 transition-colors"
                                                        onClick={(e) => { e.stopPropagation(); handleAction(() => putUnblockUser(item.user.id), '정지 해제'); }}>
                                                        정지 해제
                                                    </button>
                                                ) : (
                                                    <button className="px-4 py-2 bg-red-500 text-white text-[12px] font-bold rounded-lg hover:bg-red-600 transition-colors"
                                                        onClick={(e) => { e.stopPropagation(); handleAction(() => putBlockUser(item.user.id), '이용 정지'); }}>
                                                        이용 정지
                                                    </button>
                                                )}
                                                <button
                                                    className={`px-4 py-2 text-white text-[12px] font-bold rounded-lg transition-colors
                                                        ${item.user.isDeleted ? 'bg-achromatic-300 cursor-not-allowed' : 'bg-achromatic-600 hover:bg-achromatic-700'}`}
                                                    onClick={(e) => { e.stopPropagation(); handleAction(() => putDeleteUser(item.user.id), '탈퇴 처리'); }}
                                                    disabled={item.user.isDeleted}
                                                >
                                                    {item.user.isDeleted ? '탈퇴 완료' : '탈퇴 처리'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </tbody>
                </table>

                {/* Pagination */}
                <div className="p-4 border-t border-achromatic-100 flex justify-center">
                    <ReactPaginate
                        previousLabel={"←"}
                        nextLabel={"→"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageClick}
                        containerClassName={"flex items-center gap-1"}
                        previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100 transition-colors"}
                        nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100 transition-colors"}
                        pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100 transition-colors"}
                        activeClassName={"!bg-primary-500 !text-white"}
                        activeLinkClassName={"text-white"}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserManageComponent;